<template>
  <div class="case-section">
    <b-row class="d-flex align-items-center mb-2 mt-1 px-2">
      <b-col>
        <span class="ap-title ap-title__main">{{ $t('globalLocator.labels.caseInformation') }}</span>
      </b-col>
    </b-row>
    <b-row class="px-2">
      <b-col class="form-box mt-2">
        <div class="structure">
          <div class="structure__heading">
            <i
              class="far fa-chart-network mr-2"
              aria-hidden="true"
            />
            {{ $t('registry.cases.caseData') }}
          </div>
          <b-row class="mb-1">
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.caseLabel') }}</small>
              <h4 class="collapsable-card-title">
                {{ caseStudy.casenumber }}
              </h4>
            </b-col>
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.registryDate') }}</small>
              <h4
                v-if="caseStudy.registrationDate"
                class="collapsable-card-title"
              >
                {{ caseStudy.registrationDate | datetime }}
              </h4>
              <h4
                v-else
                class="not-available"
              >
                {{ $t('globalLocator.labels.notAvailableData') }}
              </h4>
            </b-col>
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.laboratory') }}</small>
              <h4
                v-if="caseStudy.laboratory"
                class="collapsable-card-title"
              >
                {{ caseStudy.laboratory.labName }}
              </h4>
              <h4
                v-else
                class="not-available"
              >
                {{ $t('globalLocator.labels.notAvailableData') }}
              </h4>
            </b-col>
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.register') }}</small>
              <h4
                v-if="caseStudy.registrar"
                class="collapsable-card-title"
              >
                {{ caseStudy.registrar.fullName }}
              </h4>
              <h4
                v-else
                class="not-available"
              >
                {{ $t('globalLocator.labels.notAvailableData') }}
              </h4>
            </b-col>
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.caseType') }}</small>
              <h4 class="collapsable-card-title">
                {{ caseStudy.caseType.descriptionInLaboratory }}
              </h4>
            </b-col>
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.priority') }}</small>
              <study-request-priority-dot
                show-label
                :priority="caseStudy.priority"
                :size="9"
              />
            </b-col>
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.requestLabel') }}</small>
              <div v-if="caseStudy.requestIds">
                <h4
                  v-for="(requestLabel, key) in caseStudy.requestIds"
                  :key="key"
                  class="collapsable-card-title"
                >
                  {{ requestLabel }}
                </h4>
              </div>
              <div v-else>
                <h4 class="not-available">
                  {{ $t('globalLocator.labels.notAvailableData') }}
                </h4>
              </div>
            </b-col>
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.requestDate') }}</small>
              <h4
                v-if="caseStudy.requestDate"
                class="collapsable-card-title"
              >
                {{ caseStudy.requestDate | datetime }}
              </h4>
              <h4
                v-else
                class="not-available"
              >
                {{ $t('globalLocator.labels.notAvailableData') }}
              </h4>
            </b-col>
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.requestLabel') }}</small>
              <h4
                v-if="caseStudy.requestLabel"
                class="collapsable-card-title"
              >
                {{ caseStudy.requestLabel }}
              </h4>
              <h4
                v-else
                class="not-available"
              >
                {{ $t('globalLocator.labels.notAvailableData') }}
              </h4>
            </b-col>
          </b-row>
          <div class="horizontal-divider my-3" />
          <b-row class="mb-1">
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.pathologist') }}</small>
              <h4
                v-if="caseStudy.pathologist"
                class="collapsable-card-title"
              >
                {{ caseStudy.pathologist.fullName }}
              </h4>
              <h4
                v-else
                class="not-available"
              >
                {{ $t('globalLocator.labels.notAvailableData') }}
              </h4>
            </b-col>
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.resident') }}</small>
              <h4
                v-if="caseStudy.resident"
                class="collapsable-card-title"
              >
                {{ caseStudy.resident.fullName }}
              </h4>
            </b-col>
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.informedDate') }}</small>
              <h4
                v-if="caseStudy.informedDate"
                class="collapsable-card-title"
              >
                {{ caseStudy.informedDate | datetime }}
              </h4>
              <h4
                v-else
                class="not-available"
              >
                {{ $t('globalLocator.labels.notAvailableData') }}
              </h4>
            </b-col>
            <b-col
              lg="3"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.validationDate') }}</small>
              <h4
                v-if="caseStudy.validationDate"
                class="collapsable-card-title"
              >
                {{ caseStudy.validationDate | datetime }}
              </h4>
              <h4
                v-else
                class="not-available"
              >
                {{ $t('globalLocator.labels.notAvailableData') }}
              </h4>
            </b-col>
          </b-row>
          <b-row>
            <b-col
              lg="12"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.relevantClinicalData') }}</small>
              <h4
                v-if="caseStudy.clinicalData"
                class="collapsable-card-title inside"
              >
                {{ caseStudy.clinicalData }}
              </h4>
              <h4
                v-else
                class="not-available"
              >
                {{ $t('globalLocator.labels.notAvailableData') }}
              </h4>
            </b-col>
            <b-col
              lg="12"
              class="structure__data"
            >
              <small>{{ $t('globalLocator.labels.observations') }}</small>
              <h4
                v-if="caseStudy.notes"
                class="collapsable-card-title inside"
              >
                {{ caseStudy.notes }}
              </h4>
              <h4
                v-else
                class="not-available"
              >
                {{ $t('globalLocator.labels.notAvailableData') }}
              </h4>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <small>{{ $t('globalLocator.labels.patientAgeCase') }}</small>
              <h4 class="collapsable-card-title">
                {{ age }}
              </h4>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <b-row class="px-2">
      <b-col class="form-box">
        <div class="structure">
          <div class="structure__heading">
            <i
              class="far fa-file-export mr-2"
              aria-hidden="true"
            />
            {{ $t('globalLocator.labels.requester') }}
          </div>
          <b-row>
            <b-col>
              <small>{{ $t('globalLocator.labels.cenPhysName') }}</small>
              <h4
                class="collapsable-card-title inside"
              >
                {{ caseStudy.physicianDepartment ? caseStudy.physicianDepartment.center.name : '-' }}
              </h4>
            </b-col>
            <b-col>
              <small>{{ $t('globalLocator.labels.unit') }}</small>
              <h4
                class="collapsable-card-title inside"
              >
                {{ caseStudy.physicianDepartment ? caseStudy.physicianDepartment.name : '-' }}
              </h4>
            </b-col>
            <b-col>
              <small>{{ $t('globalLocator.labels.professional') }}</small>
              <h4
                class="collapsable-card-title inside"
              >
                {{ caseStudy.physicianPetitioner ? caseStudy.physicianPetitioner.fullName : '-' }}
              </h4>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
    <b-row class="px-2">
      <b-col class="form-box">
        <div class="structure">
          <div class="structure__heading">
            <i
              class="far fa-file-import mr-2"
              aria-hidden="true"
            />
            {{ $t('globalLocator.labels.destination') }}
          </div>
          <b-row>
            <b-col>
              <small>{{ $t('globalLocator.labels.cenPhysName') }}</small>
              <h4
                class="collapsable-card-title inside"
              >
                {{ caseStudy.destinationDepartment ? caseStudy.destinationDepartment.center.name : '-' }}
              </h4>
            </b-col>
            <b-col>
              <small>{{ $t('globalLocator.labels.unit') }}</small>
              <h4
                class="collapsable-card-title inside"
              >
                {{ caseStudy.destinationDepartment ? caseStudy.destinationDepartment.name : '-' }}
              </h4>
            </b-col>
            <b-col>
              <small>{{ $t('globalLocator.labels.receiver') }}</small>
              <h4
                class="collapsable-card-title inside"
              >
                {{ caseStudy.receiver ? caseStudy.receiver : '-' }}
              </h4>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import StudyRequestPriorityDot from '@/modules/clinicalModule/components/StatusRequestPriorityDot'
import { getPatientTimeOld } from '@/utils/fieldTransformations'

export default {
    name: 'CaseSection',
    components: {
        StudyRequestPriorityDot
    },
    props: {
        caseStudy: { type: Object, required: true }
    },
    computed: {
        age() {
            return getPatientTimeOld(this.caseStudy.patient.birthdate, this.caseStudy.registrationDate)
        }
    }
}
</script>

<style lang="scss" scoped>
    .case-section .not-available {
        font-size: font-size(very-small) !important;
        font-style: italic;
        font-weight: normal;
    }
</style>
